import {
  Mutation,
  MutationOptions,
  QueryVariables,
  request,
  useMakeMutation,
  useRequest,
} from '@shared/innmaxLib/services'
import { path, pathOr } from 'ramda'
import React from 'react'
import { useQuery, UseQueryOptions } from 'react-query'
import {
  useConvertIOccupancyFee,
  useConvertIOccupancyFeeList,
  useConvertIOrderList,
  useConvertParkingFeeDiscountList,
  useConvertParkingFeeList,
  useConvertStand,
  useConvertStandList,
} from './operation.converter'
import { BasicFeeType } from './operation.type'

export const useParkingLotList = (query?: any, options?: UseQueryOptions) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/sessionInfo/parkingLot/list/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const dataSource = useConvertStandList(data)

  return {
    ...others,
    refetch,
    isLoading,
    dataSource,
  }
}

export const useParkingLot = (id?: React.Key) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    [`/sessionInfo/parkingLot/{id}`, { id }],
    {
      enabled: !!id,
      select: (res: any) => path(['data'], res),
      cacheTime: 0,
    }
  )

  const dataSource: any = useConvertStand()(data)

  return {
    ...others,
    refetch,
    isLoading,
    dataSource,
  }
}

export const createParkingLot = (values: Partial<any>) =>
  request('/sessionInfo/parkingLot', { method: 'POST', body: values })

export const useAddParkingLot = (options: MutationOptions = {}) => {
  return useMakeMutation(createParkingLot, options)
}

export const updateParkingLot = (values: any) =>
  request(`/sessionInfo/parkingLot`, { method: 'PUT', body: values })

export const useUpdateParkingLot = (options: MutationOptions = {}) => {
  return useMakeMutation(updateParkingLot, options)
}

export const deleteParkingLot = (body: { id: number }) =>
  request(`/sessionInfo/parkingLot`, { method: 'DELETE', body })

export const useDeleteParkingLot = (options: MutationOptions = {}) => {
  return useMakeMutation(deleteParkingLot, options)
}

export const useImportParkingLot = (options: MutationOptions) => {
  const { loading, mutate } = useRequest(`/sessionInfo/parkingLot/import`, {
    ...options,
    method: 'POST',
    headers: {},
  })

  const handler = (data: { file: File }) => {
    const formData = new FormData()
    formData.append('file', data.file)
    return mutate(formData)
  }

  return [loading, handler] as Mutation
}

export const useParkingDiscountList = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/parkingFee/discount/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const dataSource = useConvertParkingFeeDiscountList(data)

  return {
    ...others,
    refetch,
    isLoading,
    dataSource,
  }
}

export const useParkingFeeList = (query?: any, options?: UseQueryOptions) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/parkingFee/area/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const dataSource = useConvertParkingFeeList(data)

  return {
    ...others,
    refetch,
    isLoading,
    dataSource,
  }
}

export const createParkingFee = (values: Partial<any>) =>
  request('/parkingFee/area', { method: 'POST', body: values })

export const useAddParkingFee = (options: MutationOptions = {}) => {
  return useMakeMutation(createParkingFee, options)
}

export const updateParkingFee = ({ id, ...values }: Partial<any> = {}) =>
  request(`/parkingFee/area/${id}`, { method: 'PUT', body: values })

export const useUpdateParkingFee = (options: MutationOptions = {}) => {
  return useMakeMutation(updateParkingFee, options)
}

export const deleteParkingFee = (id: React.Key) =>
  request(`/parkingFee/area/${id}`, { method: 'DELETE' })

export const useDeleteParkingFee = (options: MutationOptions = {}) => {
  return useMakeMutation(deleteParkingFee, options)
}

export const useParkingBasicFeeList = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/parkingFee/basic', query],
    {
      ...options,
    }
  )

  const _data = pathOr({}, ['data'], data) as BasicFeeType

  const dataSource = React.useMemo(() => {
    return {
      ..._data,
      feeStartAfterCau: _data.feeStartAfter / 1000 / 60,
      signIntervalCau: _data.signInterval / 1000 / 60 / 24,
    }
  }, [_data])

  return {
    ...others,
    refetch,
    isLoading,
    dataSource,
  }
}

export const updateParkingBasicFee = ({ id, ...values }: Partial<any> = {}) =>
  request(`/parkingFee/basic`, { method: 'PUT', body: values })

export const useUpdateParkingBasicFee = (options: MutationOptions = {}) => {
  return useMakeMutation(updateParkingBasicFee, options)
}

// occupancy-fee-controller 車格佔用費
export const useOccupancyFeeList = (query?: any, options?: UseQueryOptions) => {
  const { data, ...others } = useQuery(
    ['/airportOccupancyFee/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const dataSource = useConvertIOccupancyFeeList(data)

  return {
    ...others,
    dataSource,
  }
}

export const useOccupancyFee = (query?: any, options?: UseQueryOptions) => {
  const { data: _data, ...others } = useQuery(
    ['/airportOccupancyFee/{id}', query],
    {
      enabled: !!query?.id,
      ...options,
      select: (res: any) => path(['data'], res) as any,
    }
  )

  const dataSource = useConvertIOccupancyFee(_data)

  return {
    ...others,
    data: dataSource,
    loading: others.isLoading,
  }
}

export const useAddOccupancyFee = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/airportOccupancyFee', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useUpdateOccupancyFee = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/airportOccupancyFee/{id}', {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useDeleteOccupancyFee = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/airportOccupancyFee/{id}', {
        method: 'DELETE',
        body: values,
      }),
    options
  )
}

// 新增測試訂單
export const useAddOrderTest = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/order', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

// 訂單管理
export const useOrderList = (query?: any, options?: UseQueryOptions) => {
  const { data, ...others } = useQuery(['/order/{pageNum}/{pageSize}', query], {
    refetchOnMount: true,
    ...options,
  })

  const dataSource = useConvertIOrderList(data)

  return {
    ...others,
    dataSource,
  }
}

export const useAddOrder = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/order', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useDeleteOrder = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/order/{id}', {
        method: 'DELETE',
        body: values,
      }),
    options
  )
}

// 佔用費重置 body: { mainOrderNo }
export const useResetOccupationFee = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/order/occupationFee/reset', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

// 訂單 RFID 重綁 body: { mainOrderNo, tagInfoSeq }
export const useRewriteOrderRfid = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: { mainOrderNo: string; tagInfoSeq: number }) =>
      request('/order/rfid/rewrite', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useOrderExport = (query?: any, options?: QueryVariables) => {
  const [enabled, setIsEnabled] = React.useState(false)

  const { isLoading } = useQuery(['/order/export', { ...query }], {
    ...options,
    enabled,
    onSuccess: (response: any) => {
      if (options?.onSuccess) {
        options.onSuccess(response)
      }
      setIsEnabled(false)
    },
    onError: error => {
      console.log(error)
      setIsEnabled(false)
    },
  })

  return [
    isLoading,
    () => {
      setIsEnabled(true)
    },
  ] as [boolean, () => void]
}
